import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Ruler, Home, Layers, Plus, ArrowRight, Settings } from 'lucide-react';

const CategoriesPage = () => {
  const navigate = useNavigate();

  const mandatoryCategories = [
    {
      title: 'Brands',
      description: 'Manage tile brands and manufacturers',
      icon: Package,
      route: '/categories/brands',
      color: 'bg-blue-500',
      count: null
    },
    {
      title: 'Sizes',
      description: 'Manage tile dimensions (e.g., 40x40, 60x60)',
      icon: Ruler,
      route: '/categories/sizes',
      color: 'bg-green-500',
      count: null
    },
    {
      title: 'Use Cases',
      description: 'Manage tile installation locations',
      icon: Home,
      route: '/categories/use-cases',
      color: 'bg-purple-500',
      count: null
    },
    {
      title: 'Tile Types',
      description: 'Manage tile types and finishes',
      icon: Layers,
      route: '/categories/tile-types',
      color: 'bg-orange-500',
      count: null
    }
  ];

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-7 md:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-primary-black mb-2">
            Category Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Manage all product categories and classifications for your tile business
          </p>
        </div>

        {/* Mandatory Categories Section */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 md:mb-6">
            <div className="h-1 w-8 sm:w-10 md:w-12 bg-accent-gold rounded flex-shrink-0"></div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-primary-black">
              Mandatory Categories
            </h2>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-5 md:mb-6">
            These categories are required for all tile products and cannot be removed.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {mandatoryCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(category.route)}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-5 md:p-6 text-left group border-2 border-transparent hover:border-accent-gold"
                >
                  <div
                    className={`w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 ${category.color} rounded-lg flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-5 h-5 sm:w-5.5 sm:h-5.5 md:w-6 md:h-6 text-white" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-primary-black mb-2 group-hover:text-accent-gold transition-colors truncate">
                    {category.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                    {category.description}
                  </p>
                  <div className="flex items-center text-accent-gold font-semibold text-xs sm:text-sm group-hover:gap-2 transition-all">
                    Manage
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 group-hover:ml-2 transition-all flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Optional Categories Section */}
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5 md:mb-6">
            <div className="h-1 w-8 sm:w-10 md:w-12 bg-accent-gold rounded flex-shrink-0"></div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-primary-black">
              Optional Categories
            </h2>
          </div>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-5 md:mb-6">
            Create custom categories for additional product attributes like color, finish, origin,
            etc.
          </p>

          <button
            onClick={() => navigate('/categories/optional')}
            className="w-full bg-gradient-to-r from-accent-gold to-accent-goldLight rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-5 sm:p-6 md:p-8 text-left group"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white bg-opacity-30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform flex-shrink-0">
                  <Plus className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-primary-black mb-1 sm:mb-2 truncate">
                    Optional Categories
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-primary-charcoal line-clamp-2">
                    Create and manage custom product attributes
                  </p>
                </div>
              </div>
              <Settings className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-black group-hover:rotate-90 transition-transform duration-300 flex-shrink-0 self-end sm:self-auto" />
            </div>
          </button>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mt-8 sm:mt-10 md:mt-12">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 md:p-6 border-2 border-blue-500">
            <h4 className="font-bold text-sm sm:text-base text-primary-black mb-1 sm:mb-2 truncate">
              Mandatory Categories
            </h4>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              Required for all tile products to ensure consistent product data
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 md:p-6 border-2 border-accent-gold">
            <h4 className="font-bold text-sm sm:text-base text-primary-black mb-1 sm:mb-2 truncate">
              Optional Categories
            </h4>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              Flexible attributes you can add based on your business needs
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 md:p-6 border-2 border-green-500">
            <h4 className="font-bold text-sm sm:text-base text-primary-black mb-1 sm:mb-2 truncate">
              Protected Deletion
            </h4>
            <p className="text-xs sm:text-sm text-gray-600 line-clamp-3">
              Categories in use by products cannot be deleted until reassigned
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
